// src/components/alertesCritiques/useAlertesCritiques.js
// L'ordonnanceur des alertes critiques : quand interroger le serveur, quand
// ouvrir le panneau central, quand se taire ; et, depuis le 16/10/2026, le
// panneau latéral ouvert par le bouton « Alertes » de la barre du haut.
// Une seule instance, dans le fournisseur AlertesCritiques (deux instances
// se disputeraient le verrou navigator.locks et doubleraient les appels).
//
// - « À la connexion » n'est pas « au montage » : la coquille est remontée à
//   chaque F5 et à chaque clic de la cloche (rechargement complet). Une vraie
//   connexion se reconnaît à la session portée par le jeton (sessionId, à
//   défaut timestamp ou iat) : seule une session nouvelle rend l'échéance
//   immédiate. Un F5 ne rouvre donc rien avant l'échéance.
// - L'échéance est commune aux onglets (localStorage, par école, utilisateur
//   et année) et un seul onglet agit : celui qui tient le verrou
//   navigator.locks. Les autres attendent leur tour dans la file du verrou. Un
//   onglet qui passe en arrière-plan rend le verrou, pour qu'un onglet visible
//   prenne la main : sinon l'agent qui travaille dans un deuxième onglet ne
//   verrait jamais le panneau revenir. Avec deux fenêtres visibles côte à
//   côte, la fenêtre où l'agent travaille (celle qui a le focus) prend le
//   verrou à l'autre ({ steal: true }) ; la fenêtre dépossédée se remet en
//   file. Sans navigator.locks, chaque onglet agit, et l'échéance réservée
//   avant l'appel évite le doublon.
// - « Corriger » et « Ouvrir la demande » ferment le panneau pour un rappel
//   court (2 minutes, au plus l'intervalle) : l'agent part corriger une ligne
//   et retrouve les autres ensuite, sans attendre l'intervalle entier. Les
//   gardes de l'écran libre retiennent la réouverture tant que la fenêtre de
//   correction est ouverte. Ce retour-là ne sonne pas : l'agent est déjà à
//   l'ouvrage. Le bouton du pied (« Me rappeler dans N min ») repousse d'un
//   intervalle. Ni le clic à l'extérieur ni Échap ne ferment le panneau
//   (décisions du 13/10/2026). Sur un écran d'au moins 1 280 px, le panneau
//   central passe par le PONT : voir plus bas.
// - « Voir la liste » (une information qui porte son lien : élèves ou
//   personnel sans photo) ferme le panneau comme le bouton du pied, d'un
//   intervalle entier : rien n'y est à corriger dans l'immédiat.
// - Pour la page de suivi de l'administrateur, les gestes sont journalisés
//   (POST /api/alertes-critiques/evenements) : chaque ouverture effective du
//   panneau central ('affichage', une seule fois même sous StrictMode : elle
//   passe par les gardes de verifier), « Me rappeler » ('rappel'),
//   « Corriger », « Ouvrir la demande » et « Voir la liste » ('corriger', avec
//   la clé de la ligne). Le geste est fait avant que l'appel parte ; il n'est
//   jamais attendu et une erreur ne se voit pas. Une fermeture d'office (plus
//   rien à traiter, session finie, écran imposé) n'est pas un geste : rien
//   n'est journalisé. Le constat est journalisé par le serveur.
// - Le panneau central ne s'ouvre jamais par-dessus une autre fenêtre (tiroir
//   de la coquille, Dialog Radix, voile maison, fenêtre de fin de session) :
//   Radix rendrait inerte la fenêtre du dessus en posant pointer-events: none
//   sur le body, et un z-index plus haut passerait au-dessus de la fenêtre de
//   session (rapport critique, 3.4). On attend que l'écran soit libre.
// - Il ne s'ouvre pas non plus pendant une saisie (un champ éditable a le
//   focus et la dernière frappe date de moins de 10 s) : l'agent qui tape un
//   nom ou un montant ne doit pas voir sa frappe suivante tomber dans le
//   panneau. On réessaie au tour suivant.
// - Ouvert, il veille chaque seconde : jeton disparu (déconnexion), ou écran
//   imposé apparu dessous (« Session expirée » de SessionManager, affiché
//   avant le retrait du jeton) : il se retire, et revient vite si la session
//   continue. Sans cela il resterait au-dessus de cet écran, cliquable dans
//   une session finie.
// - Ce qui est « à traiter ici » se compte dans le navigateur, sur les lignes
//   (compterATraiter) : une ligne « corriger » d'une autre année que celle de
//   la session (l'année suivante, en pré-clôture) ne se corrige pas dans cette
//   session. Elle reste affichée, mais elle n'ouvre pas le panneau. Une
//   campagne (gravité 'campagne', À COMPLÉTER : photos manquantes) ne compte
//   jamais : ses lignes sont des informations, elles n'ouvrent ni ne font
//   sonner le panneau et s'affichent quand il s'ouvre pour autre chose.
// - Une erreur du réseau ou du serveur est silencieuse et repousse l'échéance
//   d'un intervalle : jamais de rafale d'appels.
// - React.StrictMode monte les effets deux fois en développement : l'appel et
//   le son passent par des gardes en ref, et le verrou est rendu au démontage.
//
// LE PANNEAU LATÉRAL (GO de Daniel du 16/10/2026)
// - Le bouton « Alertes » de la barre l'ouvre et le ferme. Il s'affiche sur
//   un RÉSUMÉ (détecteurs ouverts, compte à traiter) gardé par utilisateur en
//   localStorage et partagé entre onglets (événement storage). Sans résumé
//   (premier jour, nouveau navigateur), une lecture est faite au montage, une
//   seule, par l'onglet élu, écran libre, sans ouvrir le panneau central :
//   sinon le bouton resterait caché jusqu'à l'échéance. Toute réponse est
//   gardée, même à 0, pour que la pastille reste juste. Une pastille plus
//   vieille que deux intervalles, ou d'une autre année, ne s'affiche plus.
// - Panneau latéral ouvert = panneau central SUSPENDU : verifier ne fait
//   rien, et l'échéance commune est repoussée à l'ouverture, à chaque
//   relecture, à chaque « Corriger », toutes les 30 s tant qu'il est ouvert
//   dans un onglet visible (le panneau central ne s'ouvre pas non plus dans
//   un autre onglet de l'agent ; onglet caché, plus au-delà d'un intervalle)
//   et à sa fermeture : le panneau central revient un intervalle entier
//   après, et pas du tout s'il ne reste rien. Jamais 1,5 s après la
//   fermeture : le panneau latéral n'est pas un tiroir (autreFenetreOuverte).
// - « Corriger » depuis le panneau latéral : la page de correction s'ouvre
//   (navigate), le panneau RESTE ouvert (dès 1 280 px ; en dessous, il se
//   replie, avec reporter()), la ligne passe « En cours », et 'corriger' est
//   journalisé une seule fois par ligne et par ouverture du panneau.
// - Relectures, panneau ouvert : (a) l'écran redevient libre après un
//   « Corriger » (une fenêtre a été vue ouverte puis fermée), 1,5 s plus tard,
//   l'écran étant revérifié libre au moment du tir ; (b) l'ouverture par le
//   bouton, sauf si la réponse a moins de 15 s ; (c) le retour du focus ou de
//   la visibilité, si la dernière lecture a plus de 2 min ; (d) toutes les
//   5 min, onglet visible, écran libre, aucun motif de constat déplié ;
//   (e) « Actualiser ». Au moins 10 s entre deux lectures, jamais deux à la
//   fois, rien quand l'onglet est caché. Rien non plus pendant qu'une fenêtre
//   est ouverte (le panneau est en pause) : une relecture différée ou un
//   nouvel essai qui tombe alors attend sa fermeture, comme (a) ; seuls
//   l'ouverture, « Actualiser » et le constat refusé passent. Une lecture en
//   échec se retente 1 min plus tard. Les lignes « En cours » prennent fin à
//   la première relecture (faite écran libre) : ce que dit alors le serveur ;
//   sauf une ligne partie en correction pendant que la lecture était en vol
//   (la réponse précède sa correction), qui attend sa propre relecture (a).
//   Le panneau ouvert pendant une lecture de verifier (sa lecture
//   d'ouverture lui cède la place) en reçoit la réponse, ou l'échec et le
//   nouvel essai.
// - La progression du jour (progressionAlertes.js) suit chaque réponse :
//   réglée, envoyée, constatée, sortie. Une relecture du panneau ouvert qui
//   apporte une ligne à traiter jamais vue aujourd'hui joue le son 'alerte'
//   une fois (si l'école l'a activé). La lecture qui suit l'ouverture ne
//   sonne pas : l'agent vient de cliquer, il regarde. La première lecture du
//   jour non plus, quel qu'en soit le motif (panneau resté ouvert passé
//   minuit, retour dans l'onglet) : rien n'y est nouveau, comme pour le badge
//   « Nouvelle » (règle portée par progressionAlertes.js, `lus`).
// - PONT : quand le panneau central est affiché sur un écran d'au moins
//   1 280 px, son « Corriger » (ou « Ouvrir la demande ») ouvre la page de
//   correction, ferme le panneau central SANS rappel court et ouvre le
//   panneau latéral avec les mêmes données, sans appel.
// - L'état ouvert du panneau latéral est recopié en sessionStorage avec la
//   session du jeton : il survit au F5 dans l'onglet, jamais à une nouvelle
//   connexion. Jeton disparu : le panneau latéral se ferme.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { installerDeverrouillageAudio, jouerSon } from '../../utils/sonsKlasys';
import { cleStockage, journaliserEvenement, lireAlertes, memoriserReglagesSon } from './apiAlertesCritiques';
import {
  ACTIONS_A_TRAITER,
  GRAVITE_CAMPAGNE,
  cleLigne,
  compterLignesATraiter,
  composerPanneau,
  estHorsSession,
  fusionnerProgressions,
  heureParis,
  integrerReponse,
  jourParis,
  lireProgression,
  marquerConstatee,
  mettreAJourProgression,
  progressionVide,
  resumerAvancement
} from './progressionAlertes';

// Les lignes que le serveur compte dans `a_traiter` ; une campagne n'est
// jamais « à traiter » (voir progressionAlertes.js, qui porte ces règles).
export { ACTIONS_A_TRAITER, GRAVITE_CAMPAGNE, cleLigne, heureParis };

const MINUTE = 60 * 1000;
// Vérification de l'onglet élu : lecture du stockage seulement, aucun appel
// tant que l'échéance n'est pas passée.
const TOUR = 30 * 1000;
// Laisser la coquille se poser avant d'ouvrir, à l'élection d'un onglet.
const PREMIER_TOUR = 2000;
// Un tiroir de la coquille qui se ferme finit son animation de sortie.
const APRES_TIROIR = 1500;
// Les réglages de l'école, tant que le serveur ne les a pas donnés.
const INTERVALLE_DEFAUT = 30;
// Une frappe plus récente que ce délai dans un champ éditable : saisie en cours.
const SAISIE_RECENTE = 10 * 1000;
// Retour du panneau après « Corriger », en minutes (au plus l'intervalle).
const RAPPEL_COURT = 2;
// Veille d'un panneau ouvert : session finie, écran imposé apparu dessous,
// fenêtre de correction ouverte puis fermée.
const GARDE = 1000;

// --- Panneau latéral -----------------------------------------------------------
// Une fenêtre fermée après « Corriger » finit son animation de sortie.
const APRES_FENETRE = 1500;
// Une réponse plus récente : le clic qui ouvre le panneau ne relit pas.
const REPONSE_FRAICHE = 15 * 1000;
// Retour dans l'onglet : relecture si la dernière a plus de 2 minutes.
const RELECTURE_RETOUR = 2 * MINUTE;
// Relecture minutée du panneau ouvert.
const RELECTURE_MINUTEE = 5 * MINUTE;
// Jamais deux lectures à moins de 10 secondes.
const ECART_LECTURES = 10 * 1000;
// Lecture en échec : nouvel essai (« nouvel essai dans 1 min »).
const NOUVEL_ESSAI = MINUTE;
// Ces relectures doivent avoir lieu : retenues par l'écart minimal, elles
// partent dès qu'il est passé. Les autres sont simplement sautées.
const RELECTURES_DIFFERABLES = new Set(['ouverture', 'liberation', 'actualiser']);
// Ces relectures partent même si une fenêtre est vue ouverte (les autres
// attendent sa fermeture) : l'ouverture et les gestes faits dans le panneau.
const RELECTURES_SANS_PAUSE = new Set(['ouverture', 'actualiser', 'constat']);

/** Dès cette largeur (px CSS), le panneau latéral pousse la page ; en dessous, il se pose par-dessus. */
export const LARGEUR_POUSSE = 1280;
/** L'identifiant de l'<aside> du panneau latéral (aria-controls du bouton). */
export const ID_PANNEAU_LATERAL = 'panneau-alertes-critiques';

/** Délai du retour après « Corriger », en minutes, pour un intervalle donné. */
export const delaiRappelCourt = (intervalleMinutes) =>
  Math.min(Number(intervalleMinutes) || INTERVALLE_DEFAUT, RAPPEL_COURT);

// --- Stockage du navigateur ------------------------------------------------
// Toujours protégé : navigation privée, stockage plein ou interdit.

function lire(cle) {
  try {
    return localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle, valeur) {
  try {
    localStorage.setItem(cle, String(valeur));
  } catch {
    // Sans stockage, l'onglet se fie à sa copie en mémoire (voir l'échéance).
  }
}

function lireSession(cle) {
  try {
    return sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrireSession(cle, valeur) {
  try {
    sessionStorage.setItem(cle, String(valeur));
  } catch {
    // Sans stockage de session, le panneau latéral se referme au F5.
  }
}

function lireJSON(brut) {
  if (!brut) return null;
  try {
    return JSON.parse(brut);
  } catch {
    return null;
  }
}

function stockageLocal() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const cleEcheance = () => cleStockage('alertesCritiques:echeance');
const cleSession = () => cleStockage('alertesCritiques:session');
const cleIntervalle = () => cleStockage('alertesCritiques:intervalle', { parAnnee: false });
const cleResume = () => cleStockage('alertesCritiques:resume', { parAnnee: false });
const cleProgression = () => cleStockage('alertesCritiques:progression');
const clePanneauLateral = () => cleStockage('alertesCritiques:panneauLateral', { parAnnee: false });
const cleAideLaterale = () => cleStockage('alertesCritiques:aideLaterale', { parAnnee: false });

// Copie en mémoire de l'échéance : sans elle, un navigateur qui refuse le
// stockage relirait « 0 » à chaque tour et appellerait le serveur toutes les
// 30 secondes.
let echeanceEnMemoire = 0;

function lireEcheance() {
  const stockee = lire(cleEcheance());
  if (stockee === null) return echeanceEnMemoire;
  const valeur = Number(stockee);
  return Number.isFinite(valeur) ? valeur : echeanceEnMemoire;
}

function ecrireEcheance(instant) {
  echeanceEnMemoire = instant;
  ecrire(cleEcheance(), instant);
}

function intervalleMemorise() {
  const minutes = Number(lire(cleIntervalle()));
  return Number.isInteger(minutes) && minutes >= 1 ? minutes : INTERVALLE_DEFAUT;
}

// --- Ce qui est à traiter dans cette session ----------------------------------

/** L'année choisie à la connexion (celle de l'en-tête X-Annee-Scolaire-Id), ou null. */
export const anneeDeSession = () => lire('annee_scolaire_id');

/**
 * Une ligne « corriger » d'une autre année que celle de la session : l'écran
 * de correction est lié à l'année active et refuserait le lien. Elle se
 * corrige dans une session ouverte sur son année. Le constat, lui, vaut pour
 * toutes les années ouvertes.
 */
export function horsSession(ligne, anneeSession = anneeDeSession()) {
  return estHorsSession(ligne, anneeSession);
}

/**
 * Les lignes qui attendent un geste dans cette session : le compte du serveur
 * (`a_traiter`), moins les corrections d'une autre année. C'est lui qui ouvre
 * le panneau central, qui le referme quand il tombe à 0, qu'affichent son
 * en-tête et la pastille du bouton « Alertes ». Une campagne n'y entre jamais.
 */
export function compterATraiter(donnees, anneeSession = anneeDeSession()) {
  return compterLignesATraiter(donnees, anneeSession);
}

// --- Journal du suivi ----------------------------------------------------------

/**
 * Journalise un geste avec le compte « à traiter ici » du moment. Jamais
 * attendu, jamais d'erreur visible : ni une exception, ni un rejet ne
 * remontent jusqu'au panneau.
 */
function journaliser(type, donnees, cle = null) {
  try {
    const envoi = journaliserEvenement(type, { nb_a_traiter: compterATraiter(donnees), cle });
    if (envoi && typeof envoi.catch === 'function') envoi.catch(() => {});
  } catch {
    // Le journal ne compte pas pour l'agent.
  }
}

// --- Session -----------------------------------------------------------------

/** La charge utile du jeton (base64url), ou null. */
function chargeDuJeton(jeton) {
  const partie = typeof jeton === 'string' ? jeton.split('.')[1] : null;
  if (!partie) return null;
  try {
    let base64 = partie.replace(/-/g, '+').replace(/_/g, '/');
    base64 += '='.repeat((4 - (base64.length % 4)) % 4);
    const octets = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(octets));
  } catch {
    return null;
  }
}

/** La session portée par le jeton (sessionId, à défaut timestamp ou iat), ou null. */
function marqueurDeSession() {
  const charge = chargeDuJeton(lire('token'));
  const marqueur = charge?.sessionId || charge?.timestamp || charge?.iat;
  return marqueur ? String(marqueur) : null;
}

/**
 * Reconnaît une vraie connexion : la session du jeton diffère de celle déjà
 * vue dans ce navigateur. L'échéance devient alors immédiate.
 * Rend false s'il n'y a pas de jeton (rien à faire).
 */
function preparerSession() {
  if (!lire('token')) return false;
  const marqueur = marqueurDeSession();
  if (!marqueur) return true;
  const cle = cleSession();
  if (lire(cle) !== marqueur) {
    ecrire(cle, marqueur);
    ecrireEcheance(Date.now());
  }
  return true;
}

function nomDuVerrou() {
  let user = null;
  try {
    user = JSON.parse(lire('user') || 'null');
  } catch {
    user = null;
  }
  const ecole = user?.tenant_subdomain || window.location.hostname || 'default';
  const utilisateur = user?.id || user?.identifiant || 'anonyme';
  return `klasys-alertes-critiques-${ecole}-${utilisateur}`;
}

// --- Panneau latéral : ouvert dans cet onglet, pour cette session ----------------

/**
 * Le panneau latéral était ouvert dans cet onglet, dans la même session
 * (F5, clic d'une notification qui recharge la page). Une nouvelle connexion
 * le rend fermé, même dans un autre onglet resté sur l'ancienne. La coquille
 * s'en sert pour son état initial.
 */
export function panneauLateralMemorise() {
  if (!lire('token')) return false;
  const valeur = lireJSON(lireSession(clePanneauLateral()));
  return Boolean(valeur && valeur.ouvert === true && valeur.session === (marqueurDeSession() || ''));
}

function memoriserPanneauLateral(ouvert) {
  ecrireSession(clePanneauLateral(), JSON.stringify({ ouvert: Boolean(ouvert), session: marqueurDeSession() || '' }));
}

/** « Comment ça marche » a déjà été déplié d'office une fois pour cet utilisateur. */
export function aideLateraleVue() {
  return lire(cleAideLaterale()) === '1';
}

export function marquerAideLateraleVue() {
  ecrire(cleAideLaterale(), '1');
}

// --- Résumé du bouton « Alertes » ------------------------------------------------

function composerResume(donnees, intervalle) {
  const ouverts = Array.isArray(donnees?.detecteurs_ouverts)
    ? donnees.detecteurs_ouverts
      .map((d) => (typeof d === 'string' ? { id: d } : d))
      .filter((d) => d && d.id)
      .map((d) => ({ id: d.id, titre: d.titre || null, gravite: d.gravite || null }))
    : null;
  return {
    detecteurs_ouverts: ouverts,
    // Serveur d'avant detecteurs_ouverts : les détecteurs de la réponse.
    nb_detecteurs: ouverts ? ouverts.length : (donnees?.detecteurs || []).length + (donnees?.erreurs || []).length,
    compte: compterATraiter(donnees),
    annee: anneeDeSession(),
    intervalle,
    le: Date.now()
  };
}

function analyserResume(brut) {
  const resume = lireJSON(brut);
  if (!resume || typeof resume !== 'object') return null;
  const nombres = [resume.le, resume.compte, resume.nb_detecteurs];
  return nombres.every((valeur) => typeof valeur === 'number' && Number.isFinite(valeur)) ? resume : null;
}

/** Le résumé du bouton gardé pour cet utilisateur, ou null. */
export const lireResume = () => analyserResume(lire(cleResume()));

function etatDuBouton(resume, actif, maintenant) {
  if (!actif || !resume || !(resume.nb_detecteurs > 0)) return { visible: false, compte: null, expireLe: null };
  const intervalle = Number(resume.intervalle) >= 1 ? Number(resume.intervalle) : INTERVALLE_DEFAUT;
  const expireLe = resume.le + 2 * intervalle * MINUTE;
  const memeAnnee = String(resume.annee ?? '') === String(anneeDeSession() ?? '');
  return { visible: true, compte: maintenant <= expireLe && memeAnnee ? resume.compte : null, expireLe };
}

function progressionStockee() {
  const jour = jourParis();
  return lireProgression(stockageLocal(), cleProgression(), jour) || progressionVide(jour);
}

// --- L'écran est-il libre ? --------------------------------------------------

/** Rendu à l'écran (ni display: none, ni dans un ancêtre caché). */
const estRendu = (element) => element.getClientRects().length > 0;

/**
 * Un voile maison `fixed inset-0` (ModalModifierFamille à 10000, ConfirmDialog,
 * écran « Session expirée »…) : visible et qui reçoit les clics. Le fond animé
 * de la coquille, en z négatif et sans clics, n'en est pas un.
 */
function estUnVoile(element) {
  if (!estRendu(element)) return false;
  const style = window.getComputedStyle(element);
  if (style.pointerEvents === 'none' || style.visibility === 'hidden') return false;
  const z = parseInt(style.zIndex, 10);
  return Number.isNaN(z) || z >= 0;
}

/**
 * Une saisie en cours : le focus est dans un champ éditable (page-formulaire
 * d'inscription, de réinscription…) et la dernière frappe est récente. Ouvrir
 * à ce moment ferait tomber la frappe suivante dans le panneau.
 */
function saisieEnCours(derniereFrappe) {
  if (!derniereFrappe || Date.now() - derniereFrappe > SAISIE_RECENTE) return false;
  const actif = document.activeElement;
  if (!actif || actif === document.body) return false;
  if (actif.isContentEditable) return true;
  return typeof actif.matches === 'function' && actif.matches('input, textarea, select');
}

function ecranOccupe(autreFenetreOuverte, derniereFrappe) {
  if (autreFenetreOuverte) return true;
  if (typeof document === 'undefined' || !document.body) return true;
  if (saisieEnCours(derniereFrappe)) return true;
  if (document.body.style.pointerEvents === 'none') return true;
  const dialogues = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
  if (Array.from(dialogues).some(estRendu)) return true;
  return Array.from(document.querySelectorAll('.fixed.inset-0')).some(estUnVoile);
}

/**
 * Les fenêtres réellement rendues (Dialog, voile maison), hors du panneau
 * latéral lui-même. Pas le seul `pointer-events: none` du body : ce blocage
 * reste parfois collé après une décision (filet de Support.jsx), le panneau
 * resterait « en pause » sans aucune fenêtre. Pas la saisie non plus : taper
 * le motif d'un constat dans le panneau ne doit pas le mettre en pause.
 * Pas non plus une fenêtre Radix déjà fermée qui finit son animation de
 * sortie (data-state="closed", 200 ms environ) : au pont, le panneau central
 * qui s'efface faisait afficher « Une fenêtre est ouverte » pendant près
 * d'une seconde, alors qu'aucune fenêtre n'était plus ouverte.
 * Ni un Popover Radix (role="dialog" lui aussi, posé dans le calque
 * [data-radix-popper-content-wrapper]) : le menu du profil dans la barre, un
 * sélecteur de date ; il ne bloque ni la page ni le panneau.
 */
function elementsOccupants() {
  if (typeof document === 'undefined' || !document.body) return [];
  const horsPanneau = (element) => !(typeof element.closest === 'function' && element.closest(`#${ID_PANNEAU_LATERAL}`));
  const ouverte = (element) => !(typeof element.getAttribute === 'function' && element.getAttribute('data-state') === 'closed');
  const pasUnPopover = (element) => !(typeof element.closest === 'function' && element.closest('[data-radix-popper-content-wrapper]'));
  const dialogues = Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]'))
    .filter((element) => horsPanneau(element) && ouverte(element) && pasUnPopover(element) && estRendu(element));
  const voiles = Array.from(document.querySelectorAll('.fixed.inset-0'))
    .filter((element) => horsPanneau(element) && ouverte(element) && estUnVoile(element));
  return [...dialogues, ...voiles];
}

/**
 * Une fenêtre est ouverte par-dessus la page (et le panneau latéral) :
 * le panneau latéral est « en pause ». `exclus` : des éléments déjà là à
 * l'instant d'un « Corriger » (le panneau central qui se ferme, au pont).
 */
export function fenetreOuverte(exclus = null) {
  return elementsOccupants().some((element) => !exclus || !exclus.has(element));
}

/**
 * Pour le panneau latéral : vrai tant qu'une fenêtre est ouverte (veille
 * d'une seconde, un état posé seulement quand il change). L'état reste
 * LOCAL au composant qui appelle ce hook, jamais dans la coquille.
 */
export function usePanneauEnPause(actif = true) {
  const [enPause, setEnPause] = useState(false);
  useEffect(() => {
    if (!actif) {
      setEnPause(false);
      return undefined;
    }
    const mesurer = () => {
      const occupe = fenetreOuverte();
      setEnPause((precedent) => (precedent === occupe ? precedent : occupe));
    };
    mesurer();
    const veille = setInterval(mesurer, GARDE);
    return () => clearInterval(veille);
  }, [actif]);
  return enPause;
}

/** La fenêtre du navigateur fait au moins `largeur` px CSS. */
export function ecranAuMoins(largeur) {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    try {
      return window.matchMedia(`(min-width: ${largeur}px)`).matches;
    } catch {
      // Repli sur la largeur intérieure.
    }
  }
  return Number(window.innerWidth) >= largeur;
}

/** `ecranAuMoins`, suivi en direct (matchMedia, à défaut redimensionnement). */
export function useEcranAuMoins(largeur) {
  const [vrai, setVrai] = useState(() => ecranAuMoins(largeur));
  useEffect(() => {
    const mesurer = () => setVrai(ecranAuMoins(largeur));
    mesurer();
    let requete = null;
    if (typeof window.matchMedia === 'function') {
      try {
        requete = window.matchMedia(`(min-width: ${largeur}px)`);
      } catch {
        requete = null;
      }
    }
    if (requete && typeof requete.addEventListener === 'function') {
      requete.addEventListener('change', mesurer);
      return () => requete.removeEventListener('change', mesurer);
    }
    if (requete && typeof requete.addListener === 'function') {
      requete.addListener(mesurer);
      return () => requete.removeListener(mesurer);
    }
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [largeur]);
  return vrai;
}

/**
 * Les voiles maison de la page (sous #root), panneau ouvert. Radix pose alors
 * pointer-events: none sur le body et la valeur s'hérite : estUnVoile ne les
 * verrait plus. On s'en tient au pointer-events écrit sur l'élément lui-même.
 * Le voile et le cadre du panneau vivent dans un portail sous body, les
 * toasts dans #toast-root : le panneau ne se détecte pas lui-même.
 */
function voilesDeLaPage() {
  if (typeof document === 'undefined') return [];
  return Array.from(document.querySelectorAll('#root .fixed.inset-0')).filter((element) => {
    if (!estRendu(element)) return false;
    if (element.classList.contains('pointer-events-none') || element.style.pointerEvents === 'none') return false;
    const style = window.getComputedStyle(element);
    if (style.visibility === 'hidden') return false;
    const z = parseInt(style.zIndex, 10);
    return Number.isNaN(z) || z >= 0;
  });
}

const ongletVisible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';

/** La fenêtre a le focus : c'est là que l'agent travaille. */
const auPremierPlan = () => typeof document !== 'undefined'
  && typeof document.hasFocus === 'function' && document.hasFocus();

// --- Le hook -------------------------------------------------------------------

/**
 * @param {{ autreFenetreOuverte?: boolean, actif?: boolean,
 *   onPanneauOuvertChange?: (ouvert: boolean) => void }} options
 *   - autreFenetreOuverte : un tiroir de la coquille (cloche, messagerie) est
 *     ouvert, le panneau central attend ;
 *   - actif : false en année clôturée, rien ne tourne ;
 *   - onPanneauOuvertChange : la coquille suit l'état du panneau latéral.
 * @returns {{ ouvert, donnees, intervalleMinutes, fermer, retirerLigne,
 *   actualiser, pontActif, pontVersPanneauLateral, contexte }} - les props du
 *   panneau central, et `contexte`, la valeur du contexte (AlertesCritiques.jsx).
 */
export default function useAlertesCritiques({
  autreFenetreOuverte = false,
  actif = true,
  onPanneauOuvertChange = null
} = {}) {
  const navigate = useNavigate();
  const [etat, setEtat] = useState({ ouvert: false, donnees: null });
  const [panneauOuvert, setPanneauOuvert] = useState(() => Boolean(actif) && panneauLateralMemorise());
  const [resume, setResume] = useState(() => lireResume());
  const [progression, setProgression] = useState(progressionStockee);
  const [lignesEnCours, setLignesEnCours] = useState(() => new Set());
  const [lecture, setLecture] = useState({ enCours: false, luLe: null, erreur: false });
  const [ouvertureLe, setOuvertureLe] = useState(null);
  const [, setHorloge] = useState(0);
  const grandEcran = useEcranAuMoins(LARGEUR_POUSSE);

  const monteRef = useRef(false);
  const eluRef = useRef(false);
  const enCoursRef = useRef(false);
  const actualisationRef = useRef(false);
  const ouvertRef = useRef(false);
  const donneesRef = useRef(null);
  const autreFenetreRef = useRef(autreFenetreOuverte);
  const precedenteRef = useRef(autreFenetreOuverte);
  const intervalleRef = useRef(INTERVALLE_DEFAUT);
  const minuterieRef = useRef(null);
  const derniereFrappeRef = useRef(0);
  // L'instant du dernier rappel court (« Corriger ») : le retour qui suit ne sonne pas.
  const rappelCourtLeRef = useRef(0);

  // Panneau latéral.
  const actifRef = useRef(Boolean(actif));
  const panneauOuvertRef = useRef(panneauOuvert);
  const progressionRef = useRef(progression);
  const lignesEnCoursRef = useRef(lignesEnCours);
  // Une relecture du panneau latéral est en vol.
  const lectureEnVolRef = useRef(false);
  // Le début de la dernière lecture, quelle qu'elle soit (écart de 10 s).
  const derniereLectureRef = useRef(0);
  // La fin de la dernière lecture réussie dans cet onglet.
  const luLeRef = useRef(null);
  const relecturePlanifieeRef = useRef(null);
  const nouvelEssaiRef = useRef(null);
  const tirRef = useRef(null);
  // Une fenêtre a été vue ouverte depuis le dernier « Corriger ».
  const vuOccupeRef = useRef(false);
  // Retour dans l'onglet pendant qu'une fenêtre était ouverte : relire à sa fermeture.
  const relireALiberationRef = useRef(false);
  // Les fenêtres déjà là au « Corriger » (le panneau central, au pont).
  const presentsRef = useRef(new Set());
  // Les lignes déjà journalisées 'corriger' depuis l'ouverture du panneau.
  const corrigesRef = useRef(new Set());
  // L'ordre de passage des lignes « En cours » : une lecture partie avant un
  // « Corriger » ne met pas fin à la ligne corrigée pendant qu'elle était en vol.
  const passagesEnCoursRef = useRef(0);
  const passageDesLignesRef = useRef(new Map());
  const constatOuvertRef = useRef(false);
  const lectureMontageRef = useRef(false);
  const rappelOuvertRef = useRef(onPanneauOuvertChange);
  const relireRef = useRef(null);

  useEffect(() => {
    actifRef.current = Boolean(actif);
  }, [actif]);

  useEffect(() => {
    rappelOuvertRef.current = onPanneauOuvertChange;
  }, [onPanneauOuvertChange]);

  /** Échéance = maintenant + l'intervalle de l'école. */
  const reporter = useCallback(() => {
    ecrireEcheance(Date.now() + intervalleRef.current * MINUTE);
  }, []);

  /** Échéance = maintenant + le rappel court : le panneau revient vite, sans sonner. */
  const rappelerBientot = useCallback(() => {
    rappelCourtLeRef.current = Date.now();
    ecrireEcheance(Date.now() + delaiRappelCourt(intervalleRef.current) * MINUTE);
  }, []);

  /** Garde les réglages de rappel et de son que le serveur vient de donner. */
  const retenirReglages = useCallback((reglages) => {
    if (!reglages) return;
    memoriserReglagesSon(reglages);
    const minutes = Number(reglages.intervalle_minutes);
    if (Number.isInteger(minutes) && minutes >= 1) {
      intervalleRef.current = minutes;
      ecrire(cleIntervalle(), minutes);
    }
  }, []);

  const changerPanneau = useCallback((ouvert) => {
    panneauOuvertRef.current = ouvert;
    setPanneauOuvert(ouvert);
  }, []);

  const majEnCours = useCallback((transformer) => {
    const suivant = transformer(lignesEnCoursRef.current);
    if (suivant === lignesEnCoursRef.current) return;
    lignesEnCoursRef.current = suivant;
    setLignesEnCours(suivant);
  }, []);

  const viderEnCours = useCallback(() => {
    passageDesLignesRef.current = new Map();
    majEnCours((lignes) => (lignes.size === 0 ? lignes : new Set()));
  }, [majEnCours]);

  /** La ligne part en correction : « En cours », avec son ordre de passage. */
  const marquerEnCours = useCallback((k) => {
    passagesEnCoursRef.current += 1;
    passageDesLignesRef.current.set(k, passagesEnCoursRef.current);
    majEnCours((lignes) => (lignes.has(k) ? lignes : new Set(lignes).add(k)));
  }, [majEnCours]);

  /**
   * Une relecture a abouti : les lignes passées « En cours » avant son départ
   * redeviennent ce que dit le serveur. Une ligne partie en correction PENDANT
   * la lecture reste « En cours » (la réponse précède sa correction) et la
   * veille attend toujours sa fenêtre : sinon l'agent qui enchaîne les
   * « Corriger » perdrait la relecture (a) de la ligne suivante. Rend true si
   * plus rien n'est « En cours ».
   */
  const finirEnCours = useCallback((passageAuDepart) => {
    const gardees = new Set();
    passageDesLignesRef.current.forEach((passage, k) => {
      if (passage > passageAuDepart && lignesEnCoursRef.current.has(k)) gardees.add(k);
    });
    if (gardees.size === 0) {
      viderEnCours();
      return true;
    }
    passageDesLignesRef.current = new Map(
      Array.from(passageDesLignesRef.current).filter(([k]) => gardees.has(k))
    );
    majEnCours((lignes) => (lignes.size === gardees.size ? lignes : gardees));
    return false;
  }, [majEnCours, viderEnCours]);

  const majProgression = useCallback((transformer, maintenant = Date.now()) => {
    try {
      const resultat = mettreAJourProgression({
        stockage: stockageLocal(),
        cle: cleProgression(),
        memoire: progressionRef.current,
        maintenant,
        transformer
      });
      progressionRef.current = resultat.progression;
      setProgression(resultat.progression);
      return resultat;
    } catch {
      return { progression: progressionRef.current, nouvelles: [] };
    }
  }, []);

  /** Les données affichées et le résumé du bouton (gardé même à 0). */
  const appliquerDonnees = useCallback((suivantes) => {
    donneesRef.current = suivantes;
    setEtat((precedent) => ({ ...precedent, donnees: suivantes }));
    const suivant = composerResume(suivantes, intervalleRef.current);
    ecrire(cleResume(), JSON.stringify(suivant));
    setResume(suivant);
  }, []);

  /** Une réponse du serveur : données, résumé, progression. Rend les lignes nouvelles du jour. */
  const integrerLecture = useCallback((reponse) => {
    appliquerDonnees(reponse);
    const maintenant = Date.now();
    luLeRef.current = maintenant;
    setLecture((precedente) => ({ ...precedente, luLe: maintenant, erreur: false }));
    const anneeSession = anneeDeSession();
    const resultat = majProgression((base) => integrerReponse(base, reponse, { maintenant, anneeSession }), maintenant);
    return Array.isArray(resultat.nouvelles) ? resultat.nouvelles : [];
  }, [appliquerDonnees, majProgression]);

  /** Retire le panneau central sans toucher à l'échéance (session finie, autre onglet). */
  const masquer = useCallback(() => {
    if (!ouvertRef.current) return;
    ouvertRef.current = false;
    setEtat((precedent) => ({ ...precedent, ouvert: false }));
  }, []);

  /**
   * Retire le panneau central et repousse le rappel : d'un intervalle, ou du
   * rappel court ({ rappelCourt: true }). Rien n'est journalisé : c'est aussi
   * la fermeture d'office, quand il n'y a plus rien à traiter ici.
   * Rend false si le panneau était déjà fermé.
   */
  const clore = useCallback((options) => {
    if (!ouvertRef.current) return false;
    masquer();
    if (options && options.rappelCourt === true) {
      rappelerBientot();
      return true;
    }
    rappelCourtLeRef.current = 0;
    reporter();
    return true;
  }, [masquer, reporter, rappelerBientot]);

  /**
   * Le geste de l'agent sur le panneau central, journalisé pour le suivi :
   * - bouton du pied : rappel dans un intervalle ('rappel') ;
   * - « Corriger », « Ouvrir la demande » ({ rappelCourt: true, cle }) :
   *   l'agent part corriger une ligne, le panneau revient vite avec les
   *   autres ('corriger', avec la clé de la ligne) ;
   * - « Voir la liste » ({ cle }, sans rappel court) : l'agent ouvre la liste
   *   d'une information, le panneau revient dans un intervalle ('corriger',
   *   avec la clé de la ligne).
   * Ni le clic à l'extérieur ni Échap ne ferment le panneau : ils n'arrivent pas ici.
   */
  const fermer = useCallback((options) => {
    const donnees = donneesRef.current;
    if (!clore(options)) return;
    const cle = options && typeof options.cle === 'string' && options.cle ? options.cle : null;
    if ((options && options.rappelCourt === true) || cle) journaliser('corriger', donnees, cle);
    else journaliser('rappel', donnees);
  }, [clore]);

  /** Remplace les données (changement local) ; plus rien à traiter : le panneau CENTRAL disparaît. */
  const remplacerDonnees = useCallback((suivantes) => {
    appliquerDonnees(suivantes);
    if (compterATraiter(suivantes) === 0) clore();
  }, [appliquerDonnees, clore]);

  /** Panneau latéral : une lecture en échec se retente 1 min plus tard. */
  const planifierNouvelEssai = useCallback(() => {
    if (nouvelEssaiRef.current) clearTimeout(nouvelEssaiRef.current);
    nouvelEssaiRef.current = setTimeout(() => {
      nouvelEssaiRef.current = null;
      if (relireRef.current) relireRef.current('nouvel_essai');
    }, NOUVEL_ESSAI);
  }, []);

  /**
   * Une lecture de verifier (rappel ou lecture au montage) a échoué alors que
   * le panneau latéral s'est ouvert pendant l'appel : sa lecture d'ouverture
   * lui avait cédé la place (jamais deux à la fois). L'échec s'y voit et le
   * nouvel essai part, comme après ses propres lectures ; sinon il restait sur
   * son chargement, sans message, jusqu'à la relecture minutée (5 min).
   */
  const echecPendantOuverture = useCallback(() => {
    if (!monteRef.current || !panneauOuvertRef.current) return;
    setLecture((precedente) => ({ ...precedente, enCours: false, erreur: true }));
    planifierNouvelEssai();
  }, [planifierNouvelEssai]);

  const verifier = useCallback(async () => {
    if (!monteRef.current || !actifRef.current || !eluRef.current || enCoursRef.current || ouvertRef.current) return;
    // Panneau latéral ouvert : le panneau central est suspendu. Jamais deux lectures à la fois.
    if (panneauOuvertRef.current || lectureEnVolRef.current || actualisationRef.current) return;
    if (!lire('token') || !ongletVisible()) return;
    if (Date.now() < lireEcheance()) {
      // Pas de résumé pour le bouton « Alertes » (premier jour, nouveau
      // navigateur) : une lecture, une seule, écran libre, sans rien ouvrir
      // ni toucher à l'échéance.
      if (lectureMontageRef.current || lireResume()) return;
      if (ecranOccupe(autreFenetreRef.current, derniereFrappeRef.current)) return;
      lectureMontageRef.current = true;
      enCoursRef.current = true;
      derniereLectureRef.current = Date.now();
      let lue = null;
      try {
        lue = await lireAlertes();
      } catch {
        lue = null;
      } finally {
        enCoursRef.current = false;
      }
      if (!monteRef.current) return;
      if (!lue) {
        echecPendantOuverture();
        return;
      }
      retenirReglages(lue.reglages);
      integrerLecture(lue);
      return;
    }
    // Une autre fenêtre est ouverte, ou l'agent tape : on réessaie au tour suivant.
    if (ecranOccupe(autreFenetreRef.current, derniereFrappeRef.current)) return;

    enCoursRef.current = true;
    // Réservation avant l'appel : un autre onglet qui prendrait la main
    // pendant l'appel ne rappelle pas le serveur.
    reporter();
    derniereLectureRef.current = Date.now();
    let reponse = null;
    try {
      reponse = await lireAlertes();
    } catch {
      reponse = null;
    } finally {
      enCoursRef.current = false;
    }
    if (!monteRef.current) return;
    if (!reponse) {
      // Silencieux pour le panneau central, l'échéance est déjà repoussée.
      echecPendantOuverture();
      return;
    }

    const reglages = reponse.reglages || {};
    retenirReglages(reglages);
    reporter();
    // Gardée même à 0 : la pastille du bouton suit la dernière réponse.
    integrerLecture(reponse);
    // Seul ce qui se traite dans cette session ouvre le panneau.
    if (compterATraiter(reponse) === 0) return;
    // Le panneau latéral s'est ouvert pendant l'appel : le panneau central reste suspendu.
    if (panneauOuvertRef.current) return;

    // L'écran a pu se remplir pendant l'appel, l'agent se mettre à taper ou
    // l'onglet passer derrière : on n'ouvre pas, on réessaie dès que possible.
    if (!eluRef.current || !ongletVisible() || ecranOccupe(autreFenetreRef.current, derniereFrappeRef.current)) {
      ecrireEcheance(Date.now());
      return;
    }
    ouvertRef.current = true;
    setEtat({ ouvert: true, donnees: reponse });
    journaliser('affichage', reponse);
    // Retour dans l'intervalle qui suit un « Corriger » : l'agent est à
    // l'ouvrage, le panneau revient sans sonner. Au-delà, rappel ordinaire.
    const silencieux = Date.now() - rappelCourtLeRef.current < intervalleRef.current * MINUTE;
    rappelCourtLeRef.current = 0;
    if (!silencieux && reglages.son_active !== false) jouerSon('alerte');
  }, [reporter, retenirReglages, integrerLecture, echecPendantOuverture]);

  // --- Relectures du panneau latéral -------------------------------------------------

  const planifierRelecture = useCallback((motif, delai) => {
    // Une seule relecture en attente : elle vaut pour toutes.
    if (relecturePlanifieeRef.current) return;
    const entree = { motif, minuterie: null };
    entree.minuterie = setTimeout(() => {
      if (relecturePlanifieeRef.current !== entree) return;
      relecturePlanifieeRef.current = null;
      if (relireRef.current) relireRef.current(entree.motif);
    }, Math.max(0, delai));
    relecturePlanifieeRef.current = entree;
  }, []);

  const annulerRelectures = useCallback(() => {
    if (relecturePlanifieeRef.current) {
      clearTimeout(relecturePlanifieeRef.current.minuterie);
      relecturePlanifieeRef.current = null;
    }
    if (nouvelEssaiRef.current) {
      clearTimeout(nouvelEssaiRef.current);
      nouvelEssaiRef.current = null;
    }
    if (tirRef.current) {
      clearTimeout(tirRef.current);
      tirRef.current = null;
    }
  }, []);

  /**
   * Relit les alertes du panneau latéral ouvert. `motif` : 'ouverture',
   * 'liberation' (a), 'retour' (c), 'minuterie' (d), 'actualiser' (e),
   * 'nouvel_essai', 'constat' (constat refusé, { force: true } : sans l'écart
   * de 10 s). Rend true si les données ont été relues.
   */
  const relire = useCallback(async (motif, { force = false } = {}) => {
    const reprendre = () => {
      if (motif === 'nouvel_essai' && panneauOuvertRef.current) planifierNouvelEssai();
    };
    if (!monteRef.current || !actifRef.current || !panneauOuvertRef.current) return false;
    if (!lire('token') || !ongletVisible()) {
      reprendre();
      return false;
    }
    // Une fenêtre est ouverte, le panneau est en pause : la relecture attend
    // sa fermeture (elle devient la relecture (a)). Une relecture différée par
    // l'écart de 10 s ou un nouvel essai qui tomberait pendant la fenêtre de
    // correction de la ligne suivante lui retirerait son « En cours » avant
    // la correction, et la relecture de sa fermeture n'aurait plus lieu.
    // L'ouverture (rien n'est encore « En cours »), « Actualiser » et le
    // constat refusé (gestes dans le panneau, qu'aucune fenêtre ne couvre
    // donc) passent.
    if (!RELECTURES_SANS_PAUSE.has(motif) && fenetreOuverte(presentsRef.current)) {
      relireALiberationRef.current = true;
      vuOccupeRef.current = true;
      return false;
    }
    if (lectureEnVolRef.current || enCoursRef.current || actualisationRef.current) {
      // La lecture en vol apporte ce qu'il faut, sauf après une fenêtre fermée.
      if (motif === 'liberation') planifierRelecture(motif, ECART_LECTURES);
      else reprendre();
      return false;
    }
    const ecart = Date.now() - derniereLectureRef.current;
    if (!force && ecart < ECART_LECTURES) {
      if (RELECTURES_DIFFERABLES.has(motif)) planifierRelecture(motif, ECART_LECTURES - ecart);
      else reprendre();
      return false;
    }
    if (relecturePlanifieeRef.current) {
      clearTimeout(relecturePlanifieeRef.current.minuterie);
      relecturePlanifieeRef.current = null;
    }
    if (nouvelEssaiRef.current) {
      clearTimeout(nouvelEssaiRef.current);
      nouvelEssaiRef.current = null;
    }

    lectureEnVolRef.current = true;
    derniereLectureRef.current = Date.now();
    const passageAuDepart = passagesEnCoursRef.current;
    setLecture((precedente) => (precedente.enCours ? precedente : { ...precedente, enCours: true }));
    reporter();
    let reponse = null;
    try {
      reponse = await lireAlertes();
    } catch {
      reponse = null;
    } finally {
      lectureEnVolRef.current = false;
    }
    if (!monteRef.current) return false;
    if (!reponse) {
      setLecture((precedente) => ({ ...precedente, enCours: false, erreur: true }));
      if (panneauOuvertRef.current) planifierNouvelEssai();
      return false;
    }
    const reglages = reponse.reglages || {};
    retenirReglages(reglages);
    if (panneauOuvertRef.current) reporter();
    const nouvelles = integrerLecture(reponse);
    setLecture((precedente) => ({ ...precedente, enCours: false }));
    // Relecture faite écran libre : les lignes « En cours » redeviennent ce
    // que dit le serveur (réglée, envoyée, ou encore à traiter). Une ligne
    // partie en correction pendant la lecture reste « En cours », et la
    // veille garde ce qu'elle a vu de sa fenêtre.
    if (finirEnCours(passageAuDepart)) {
      vuOccupeRef.current = false;
      relireALiberationRef.current = false;
    }
    if (panneauOuvertRef.current && motif !== 'ouverture' && nouvelles.length > 0 && reglages.son_active !== false) {
      jouerSon('alerte');
    }
    return true;
  }, [planifierRelecture, planifierNouvelEssai, reporter, retenirReglages, integrerLecture, finirEnCours]);

  useEffect(() => {
    relireRef.current = relire;
  }, [relire]);

  /**
   * Relit les alertes du panneau ouvert, sans toucher à l'échéance ni sonner :
   * un constat refusé parce que la situation a changé. Vaut pour les deux
   * panneaux. Rend true si le panneau a été mis à jour.
   */
  const actualiser = useCallback(async () => {
    if (ouvertRef.current) {
      if (actualisationRef.current) return false;
      actualisationRef.current = true;
      derniereLectureRef.current = Date.now();
      let reponse = null;
      try {
        reponse = await lireAlertes();
      } catch {
        reponse = null;
      } finally {
        actualisationRef.current = false;
      }
      // Fermé ou démonté pendant l'appel : le prochain rappel relira tout.
      if (!monteRef.current || !reponse || !ouvertRef.current) return false;
      retenirReglages(reponse.reglages);
      integrerLecture(reponse);
      if (compterATraiter(reponse) === 0) clore();
      return true;
    }
    if (panneauOuvertRef.current) return relire('constat', { force: true });
    return false;
  }, [retenirReglages, integrerLecture, clore, relire]);

  /** Après un constat (ou un constat déjà fait ailleurs) : la ligne quitte la liste, « Constatée » du jour. */
  const retirerLigne = useCallback((detecteurId, cle) => {
    const actuelles = donneesRef.current;
    if (!actuelles) return;
    let retiree = null;
    let detecteurDeLaLigne = null;
    let rang = 0;
    const detecteurs = (actuelles.detecteurs || [])
      .map((detecteur) => {
        if (detecteur.id !== detecteurId) return detecteur;
        const lignes = (detecteur.lignes || []).filter((ligne, index) => {
          if (!retiree && ligne.cle === cle) {
            retiree = ligne;
            detecteurDeLaLigne = detecteur;
            rang = index;
            return false;
          }
          return true;
        });
        if (!retiree) return detecteur;
        const total = Number.isFinite(Number(detecteur.total)) ? Number(detecteur.total) - 1 : lignes.length;
        return { ...detecteur, lignes, total: Math.max(lignes.length, total) };
      })
      .filter((detecteur) => (detecteur.lignes || []).length > 0);
    if (!retiree) return;

    const maintenant = Date.now();
    majProgression((base) => ({
      progression: marquerConstatee(base, { detecteur: detecteurDeLaLigne, ligne: retiree, rang, maintenant })
    }), maintenant);
    const k = cleLigne(detecteurId, cle);
    majEnCours((lignes) => {
      if (!lignes.has(k)) return lignes;
      const suivantes = new Set(lignes);
      suivantes.delete(k);
      return suivantes;
    });

    const compte = Number(actuelles.a_traiter) || 0;
    remplacerDonnees({
      ...actuelles,
      detecteurs,
      a_traiter: Math.max(0, compte - (ACTIONS_A_TRAITER.has(retiree.action) ? 1 : 0))
    });
  }, [remplacerDonnees, majProgression, majEnCours]);

  // --- Ouverture et fermeture du panneau latéral -----------------------------------------

  const ouvrirInterne = useCallback(({ pont = false } = {}) => {
    if (!actifRef.current || panneauOuvertRef.current || !lire('token')) return false;
    // Le panneau central est suspendu tant que le panneau latéral est ouvert.
    masquer();
    changerPanneau(true);
    const maintenant = Date.now();
    setOuvertureLe(maintenant);
    corrigesRef.current = new Set();
    viderEnCours();
    vuOccupeRef.current = false;
    relireALiberationRef.current = false;
    constatOuvertRef.current = false;
    presentsRef.current = new Set();
    setLecture((precedente) => (precedente.erreur ? { ...precedente, erreur: false } : precedente));
    reporter();
    if (!pont) {
      const fraiche = donneesRef.current && luLeRef.current !== null && maintenant - luLeRef.current < REPONSE_FRAICHE;
      if (!fraiche) relire('ouverture');
    }
    return true;
  }, [masquer, changerPanneau, viderEnCours, reporter, relire]);

  /**
   * Ferme le panneau latéral. Le panneau central revient un intervalle entier
   * après (reporter), sauf fin de session ({ raison: 'session' }).
   */
  const fermerInterne = useCallback((options) => {
    if (!panneauOuvertRef.current) return;
    changerPanneau(false);
    annulerRelectures();
    viderEnCours();
    vuOccupeRef.current = false;
    relireALiberationRef.current = false;
    constatOuvertRef.current = false;
    presentsRef.current = new Set();
    setLecture((precedente) => (precedente.erreur ? { ...precedente, erreur: false } : precedente));
    if (!options || options.raison !== 'session') {
      rappelCourtLeRef.current = 0;
      reporter();
    }
  }, [changerPanneau, annulerRelectures, viderEnCours, reporter]);

  const ouvrirPanneau = useCallback(() => {
    ouvrirInterne();
  }, [ouvrirInterne]);

  const fermerPanneau = useCallback(() => {
    fermerInterne();
  }, [fermerInterne]);

  const basculerPanneau = useCallback(() => {
    if (panneauOuvertRef.current) fermerInterne();
    else ouvrirInterne();
  }, [ouvrirInterne, fermerInterne]);

  const relirePanneau = useCallback(() => relire('actualiser'), [relire]);

  /**
   * « Corriger » ou « Ouvrir la demande » depuis le panneau latéral : la page
   * de correction s'ouvre, le panneau reste ouvert (ou se replie sous
   * 1 280 px), la ligne passe « En cours ».
   */
  const corriger = useCallback(({ detecteurId, cle, lien } = {}) => {
    if (!panneauOuvertRef.current || !lien || !cle) return;
    navigate(lien);
    const k = cleLigne(detecteurId, cle);
    presentsRef.current = new Set(elementsOccupants());
    vuOccupeRef.current = false;
    marquerEnCours(k);
    if (!corrigesRef.current.has(k)) {
      corrigesRef.current.add(k);
      journaliser('corriger', donneesRef.current, cle);
    }
    reporter();
    if (!ecranAuMoins(LARGEUR_POUSSE)) fermerInterne();
  }, [navigate, marquerEnCours, reporter, fermerInterne]);

  /** « Voir la liste » depuis le panneau latéral : la liste filtrée ; rien n'est « En cours ». */
  const voirListe = useCallback(({ detecteurId, cle, lien } = {}) => {
    if (!panneauOuvertRef.current || !lien) return;
    navigate(lien);
    const k = cleLigne(detecteurId, cle);
    if (cle && !corrigesRef.current.has(k)) {
      corrigesRef.current.add(k);
      journaliser('corriger', donneesRef.current, cle);
    }
    reporter();
    if (!ecranAuMoins(LARGEUR_POUSSE)) fermerInterne();
  }, [navigate, reporter, fermerInterne]);

  /**
   * PONT : « Corriger » ou « Ouvrir la demande » du panneau central, écran
   * d'au moins 1 280 px. Rend true si le pont est pris : page de correction
   * ouverte, panneau central fermé sans rappel court, panneau latéral ouvert
   * avec les mêmes données (sans appel), ligne « En cours », 'corriger'
   * journalisé. Rend false sinon : l'appelant garde le comportement d'avant
   * (navigate, puis onFermer({ rappelCourt: true, cle })).
   */
  const pontVersPanneauLateral = useCallback(({ detecteurId, cle, lien } = {}) => {
    if (!actifRef.current || !ouvertRef.current || !lien || !lire('token') || !ecranAuMoins(LARGEUR_POUSSE)) return false;
    navigate(lien);
    // Le panneau central se ferme : ses éléments, encore là pendant leur
    // animation de sortie, ne comptent pas comme la fenêtre de correction.
    const presents = new Set(elementsOccupants());
    masquer();
    rappelCourtLeRef.current = 0;
    if (!panneauOuvertRef.current) ouvrirInterne({ pont: true });
    presentsRef.current = presents;
    vuOccupeRef.current = false;
    if (cle) {
      const k = cleLigne(detecteurId, cle);
      marquerEnCours(k);
      corrigesRef.current.add(k);
      journaliser('corriger', donneesRef.current, cle);
    }
    reporter();
    return true;
  }, [navigate, masquer, ouvrirInterne, marquerEnCours, reporter]);

  /** Un motif de constat est déplié dans le panneau latéral : pas de relecture minutée. */
  const signalerConstatOuvert = useCallback((ouvert) => {
    constatOuvertRef.current = Boolean(ouvert);
  }, []);

  // --- Effets -------------------------------------------------------------------------

  // Montage : son, session, élection de l'onglet, tours de vérification.
  useEffect(() => {
    monteRef.current = true;
    if (!actif) {
      return () => {
        monteRef.current = false;
      };
    }
    installerDeverrouillageAudio();
    intervalleRef.current = intervalleMemorise();
    if (!preparerSession()) {
      return () => {
        monteRef.current = false;
      };
    }

    let enVie = true;
    // La demande du verrou de cet onglet : en file, ou tenue (`liberer` posé).
    let demande = null;
    const verrous = typeof navigator !== 'undefined' && navigator.locks
      && typeof navigator.locks.request === 'function' ? navigator.locks : null;
    const nom = nomDuVerrou();

    const planifier = (delai) => {
      clearTimeout(minuterieRef.current);
      minuterieRef.current = setTimeout(() => { verifier(); }, delai);
    };

    const devenirElu = () => {
      eluRef.current = true;
      planifier(PREMIER_TOUR);
    };

    /**
     * Se mettre en file pour le verrou ; `prendre` : le prendre aussitôt à
     * l'onglet qui le tient (fenêtre où l'agent travaille). `steal` ne se
     * combine pas avec `signal` : une prise de main n'attend pas en file.
     */
    const demander = ({ prendre = false } = {}) => {
      if (!enVie) return;
      if (!verrous) {
        devenirElu();
        return;
      }
      if (demande) {
        // Déjà élu, ou déjà en file sans besoin de passer devant.
        if (demande.liberer || !prendre) return;
        demande.controleur.abort();
        demande = null;
      }
      const courante = { controleur: new AbortController(), liberer: null };
      demande = courante;
      verrous
        .request(nom, prendre ? { steal: true } : { signal: courante.controleur.signal }, () => new Promise((resolve) => {
          // Démonté, rendu, remplacé, ou passé derrière en attendant son tour : on rend aussitôt.
          if (demande !== courante || !enVie || document.visibilityState === 'hidden') {
            if (demande === courante) demande = null;
            resolve();
            return;
          }
          courante.liberer = resolve;
          devenirElu();
        }))
        .catch(() => {
          // AbortError : demande retirée de la file, ou verrou pris par une
          // autre fenêtre où l'agent travaille désormais.
          if (demande !== courante) return;
          const etaitElu = Boolean(courante.liberer);
          demande = null;
          eluRef.current = false;
          // Dépossédé : on se remet en file, pour reprendre la main quand
          // l'autre fenêtre partira. Jamais après un refus sans élection :
          // un verrou qui échoue toujours ne doit pas tourner en boucle.
          if (etaitElu && enVie && ongletVisible()) demander();
        });
    };

    const rendre = () => {
      eluRef.current = false;
      const courante = demande;
      demande = null;
      if (!courante) return;
      // Tenu : on le rend. En file : on se retire (une prise de main en
      // cours se rendra d'elle-même, `demande` ne la désigne plus).
      if (courante.liberer) courante.liberer();
      else courante.controleur.abort();
    };

    const surVisibilite = () => {
      if (document.visibilityState === 'visible') {
        if (eluRef.current) verifier();
        else demander({ prendre: auPremierPlan() });
      } else if (verrous) {
        rendre();
      }
    };

    // L'agent revient dans cette fenêtre : elle prend la main, même si une
    // autre fenêtre restée visible (deuxième écran) la tenait.
    const surFocus = () => {
      if (!verrous || eluRef.current || !ongletVisible()) return;
      demander({ prendre: true });
    };

    const surStockage = (evenement) => {
      // Déconnexion dans un autre onglet : le panneau n'a plus lieu d'être.
      if (evenement.key === 'token' && !evenement.newValue) {
        masquer();
        return;
      }
      // Un autre onglet a repoussé l'échéance ou pris la main : le panneau
      // resté ouvert dans cet onglet caché se retire.
      if (evenement.key === cleEcheance() && ouvertRef.current && document.visibilityState === 'hidden') {
        masquer();
      }
    };

    // L'instant de la dernière frappe, pour ne pas ouvrir pendant une saisie.
    const surFrappe = () => {
      derniereFrappeRef.current = Date.now();
    };

    const tour = setInterval(() => { verifier(); }, TOUR);

    document.addEventListener('visibilitychange', surVisibilite);
    document.addEventListener('keydown', surFrappe, true);
    window.addEventListener('storage', surStockage);
    window.addEventListener('focus', surFocus);
    if (document.visibilityState !== 'hidden') demander({ prendre: auPremierPlan() });

    return () => {
      enVie = false;
      monteRef.current = false;
      clearInterval(tour);
      clearTimeout(minuterieRef.current);
      document.removeEventListener('visibilitychange', surVisibilite);
      document.removeEventListener('keydown', surFrappe, true);
      window.removeEventListener('storage', surStockage);
      window.removeEventListener('focus', surFocus);
      rendre();
    };
  }, [verifier, masquer, actif]);

  // Panneau central ouvert : veille chaque seconde. Jeton disparu : il se retire.
  // Écran imposé apparu dessous (« Session expirée », affiché avant le retrait
  // du jeton) : il se retire et revient vite si la session continue. Seuls
  // comptent les voiles apparus après l'ouverture : aucun n'était là à
  // l'ouverture (écran libre), et un élément déjà présent qui n'en est pas un
  // ne doit pas faire tourner le panneau en rond.
  useEffect(() => {
    if (!etat.ouvert) return undefined;
    const presents = new Set(voilesDeLaPage());
    const garde = setInterval(() => {
      if (!ouvertRef.current) return;
      if (!lire('token')) {
        masquer();
        return;
      }
      if (voilesDeLaPage().some((voile) => !presents.has(voile))) {
        masquer();
        rappelerBientot();
      }
    }, GARDE);
    return () => clearInterval(garde);
  }, [etat.ouvert, masquer, rappelerBientot]);

  // Un tiroir de la coquille se ferme : on regarde aussitôt, sans attendre le tour.
  useEffect(() => {
    autreFenetreRef.current = autreFenetreOuverte;
    const vientDeSeFermer = precedenteRef.current && !autreFenetreOuverte;
    precedenteRef.current = autreFenetreOuverte;
    if (!vientDeSeFermer) return undefined;
    const minuterie = setTimeout(() => { verifier(); }, APRES_TIROIR);
    return () => clearTimeout(minuterie);
  }, [autreFenetreOuverte, verifier]);

  // L'état du panneau latéral : recopié pour le F5 de cet onglet, et transmis à la coquille.
  useEffect(() => {
    memoriserPanneauLateral(panneauOuvert);
    if (typeof rappelOuvertRef.current === 'function') rappelOuvertRef.current(panneauOuvert);
  }, [panneauOuvert]);

  // Année clôturée (ou garde levée) : plus rien d'ouvert.
  useEffect(() => {
    if (actif) return;
    masquer();
    if (panneauOuvertRef.current) {
      changerPanneau(false);
      annulerRelectures();
    }
  }, [actif, masquer, changerPanneau, annulerRelectures]);

  // Panneau latéral rétabli ouvert au montage (F5 dans la même session) : il
  // suspend le panneau central dès maintenant et lit ses données.
  useEffect(() => {
    if (!actif || !panneauOuvertRef.current) return;
    if (!lire('token')) {
      changerPanneau(false);
      return;
    }
    setOuvertureLe((precedent) => (precedent === null ? Date.now() : precedent));
    reporter();
    if (!donneesRef.current) relire('ouverture');
  }, [actif, changerPanneau, reporter, relire]);

  // Panneau latéral ouvert, chaque seconde : jeton disparu, il se ferme ;
  // après un « Corriger », une fenêtre vue ouverte puis fermée déclenche la
  // relecture (a), 1,5 s plus tard, l'écran étant revérifié libre au tir.
  useEffect(() => {
    if (!panneauOuvert || !actif) return undefined;
    const annulerTir = () => {
      if (tirRef.current) {
        clearTimeout(tirRef.current);
        tirRef.current = null;
      }
    };
    const veille = setInterval(() => {
      if (!panneauOuvertRef.current) return;
      if (!lire('token')) {
        fermerInterne({ raison: 'session' });
        return;
      }
      if (lignesEnCoursRef.current.size === 0 && !relireALiberationRef.current) return;
      if (fenetreOuverte(presentsRef.current)) {
        vuOccupeRef.current = true;
        annulerTir();
        return;
      }
      if (!vuOccupeRef.current || tirRef.current) return;
      tirRef.current = setTimeout(() => {
        tirRef.current = null;
        // Une fenêtre s'est rouverte (la page de correction charge sa fiche) :
        // on attend sa fermeture. Onglet caché : la relecture partira au retour.
        if (!panneauOuvertRef.current || fenetreOuverte(presentsRef.current) || !ongletVisible()) return;
        vuOccupeRef.current = false;
        relireALiberationRef.current = false;
        relire('liberation');
      }, APRES_FENETRE);
    }, GARDE);
    return () => {
      clearInterval(veille);
      annulerTir();
    };
  }, [panneauOuvert, actif, fermerInterne, relire]);

  // Panneau latéral ouvert, toutes les 30 s, onglet visible : le panneau
  // central reste suspendu (ici et dans les autres onglets, deux fenêtres
  // côte à côte comprises), et relecture (d) quand la dernière lecture a
  // 5 minutes. Onglet caché, l'échéance n'est plus repoussée : un panneau
  // latéral oublié dans un onglet de fond ne fait pas taire les alertes des
  // autres onglets plus d'un intervalle (il ne relit pas, il ne sonne pas).
  // Dans cet onglet-ci, verifier reste suspendu tant qu'il est ouvert.
  useEffect(() => {
    if (!panneauOuvert || !actif) return undefined;
    const tour = setInterval(() => {
      if (!panneauOuvertRef.current || !ongletVisible()) return;
      reporter();
      if (constatOuvertRef.current || fenetreOuverte()) return;
      if (Date.now() - derniereLectureRef.current >= RELECTURE_MINUTEE) relire('minuterie');
    }, TOUR);
    return () => clearInterval(tour);
  }, [panneauOuvert, actif, reporter, relire]);

  // Panneau latéral ouvert : retour du focus ou de la visibilité (c).
  useEffect(() => {
    if (!panneauOuvert || !actif) return undefined;
    const auRetour = () => {
      if (!panneauOuvertRef.current || !ongletVisible()) return;
      if (luLeRef.current !== null && Date.now() - luLeRef.current <= RELECTURE_RETOUR) return;
      if (fenetreOuverte(presentsRef.current)) {
        // Une fenêtre est ouverte : relire à sa fermeture.
        relireALiberationRef.current = true;
        vuOccupeRef.current = true;
        return;
      }
      relire('retour');
    };
    document.addEventListener('visibilitychange', auRetour);
    window.addEventListener('focus', auRetour);
    return () => {
      document.removeEventListener('visibilitychange', auRetour);
      window.removeEventListener('focus', auRetour);
    };
  }, [panneauOuvert, actif, relire]);

  // Les autres onglets : résumé du bouton, progression du jour, déconnexion.
  useEffect(() => {
    if (!actif) return undefined;
    const surStockage = (evenement) => {
      const cle = evenement.key;
      if (cle === null) {
        // Stockage vidé (déconnexion d'un autre onglet).
        setResume(lireResume());
        if (!lire('token')) fermerInterne({ raison: 'session' });
        return;
      }
      if (cle === cleResume()) {
        setResume(analyserResume(evenement.newValue));
        return;
      }
      if (cle === cleProgression()) {
        const suivante = fusionnerProgressions(lireJSON(evenement.newValue), progressionRef.current, jourParis());
        progressionRef.current = suivante;
        setProgression(suivante);
        return;
      }
      if (cle === 'token' && !evenement.newValue) fermerInterne({ raison: 'session' });
    };
    window.addEventListener('storage', surStockage);
    return () => window.removeEventListener('storage', surStockage);
  }, [actif, fermerInterne]);

  // La pastille s'efface quand le résumé passe deux intervalles.
  const bouton = etatDuBouton(resume, actif, Date.now());
  useEffect(() => {
    if (bouton.compte === null || !bouton.expireLe) return undefined;
    const delai = bouton.expireLe - Date.now() + 50;
    if (delai <= 0 || delai > 0x7fffffff) return undefined;
    const minuterie = setTimeout(() => setHorloge((horloge) => horloge + 1), delai);
    return () => clearTimeout(minuterie);
  }, [bouton.compte, bouton.expireLe]);

  // --- Ce que lisent les panneaux et le bouton ---------------------------------------------

  const minutes = Number(etat.donnees?.reglages?.intervalle_minutes);
  const intervalleMinutes = Number.isInteger(minutes) && minutes >= 1 ? minutes : intervalleRef.current;
  const sonActif = etat.donnees?.reglages ? etat.donnees.reglages.son_active !== false : true;
  const lateralOuvert = Boolean(actif) && panneauOuvert;
  const anneeSession = anneeDeSession();

  const groupes = useMemo(
    () => (lateralOuvert
      ? composerPanneau(etat.donnees, progression, { enCours: lignesEnCours, depuis: ouvertureLe, anneeSession })
      : []),
    [lateralOuvert, etat.donnees, progression, lignesEnCours, ouvertureLe, anneeSession]
  );
  const avancement = useMemo(
    () => resumerAvancement(progression, etat.donnees, anneeSession),
    [progression, etat.donnees, anneeSession]
  );

  const contexte = useMemo(() => ({
    actif: Boolean(actif),
    // Bouton « Alertes »
    boutonVisible: bouton.visible,
    compteBouton: bouton.compte,
    // Panneau latéral
    panneauOuvert: lateralOuvert,
    ouvrirPanneau,
    fermerPanneau,
    basculerPanneau,
    donnees: etat.donnees,
    groupes,
    avancement,
    enCours: lignesEnCours,
    intervalleMinutes,
    sonActif,
    grandEcran,
    lectureEnCours: lecture.enCours,
    premiereLecture: lateralOuvert && !etat.donnees && !lecture.erreur,
    luLe: lecture.luLe,
    erreurLecture: lecture.erreur,
    relirePanneau,
    actualiser,
    corriger,
    voirListe,
    retirerLigne,
    signalerConstatOuvert
  }), [
    actif, bouton.visible, bouton.compte, lateralOuvert, ouvrirPanneau, fermerPanneau, basculerPanneau,
    etat.donnees, groupes, avancement, lignesEnCours, intervalleMinutes, sonActif, grandEcran,
    lecture.enCours, lecture.erreur, lecture.luLe, relirePanneau, actualiser, corriger, voirListe,
    retirerLigne, signalerConstatOuvert
  ]);

  return {
    ouvert: etat.ouvert,
    donnees: etat.donnees,
    intervalleMinutes,
    fermer,
    retirerLigne,
    actualiser,
    pontActif: Boolean(actif) && grandEcran,
    pontVersPanneauLateral,
    contexte
  };
}
